import { LANDING_WEB_ORIGIN } from '../../../config/domainConfig';
import { UNSUBSCRIBE_MERGE_TAG } from '../../../utils/unsubscribeUrl';

export type ComplianceFooterVariant = 'classic' | 'divided';

export const DEFAULT_COMPLIANCE_FOOTER_VARIANT: ComplianceFooterVariant = 'classic';

export const COMPLIANCE_FOOTER_VARIANTS: Array<{
  id: ComplianceFooterVariant;
  label: string;
  description: string;
}> = [
  {
    id: 'classic',
    label: 'Classic',
    description: 'Centered stack with address, unsubscribe, and badge.',
  },
  {
    id: 'divided',
    label: 'Divided',
    description: 'Address on top; unsubscribe and badge on one row.',
  },
];

const REMOVED_VARIANTS = new Set(['bordered', 'minimal', 'compact']);

export function normalizeComplianceFooterVariant(
  value?: string | null
): ComplianceFooterVariant {
  if (value && REMOVED_VARIANTS.has(value)) return DEFAULT_COMPLIANCE_FOOTER_VARIANT;
  if (value === 'divided') return 'divided';
  return DEFAULT_COMPLIANCE_FOOTER_VARIANT;
}

/** Picks readable muted text on light vs dark backgrounds. */
export function getComplianceFooterContrastColor(backgroundColor?: string | null): string {
  const hexColor = (backgroundColor || '').trim();
  if (!hexColor || hexColor.toLowerCase() === 'transparent') return '#64748B';

  if (hexColor.startsWith('rgb')) {
    const matches = hexColor.match(/\d+/g);
    if (!matches || matches.length < 3) return '#64748B';
    const r = parseInt(matches[0], 10);
    const g = parseInt(matches[1], 10);
    const b = parseInt(matches[2], 10);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#64748B' : '#CBD5E1';
  }

  const hex = hexColor.replace('#', '');
  if (hex.length < 6) return '#64748B';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#64748B';
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#64748B' : '#CBD5E1';
}

export function resolveComplianceFooterTextColor(styles: {
  backgroundColor?: string;
  contentBackgroundColor?: string;
  complianceFooterTextColor?: string;
}): string {
  const custom = styles.complianceFooterTextColor?.trim();
  if (custom) return custom;
  const bg = styles.contentBackgroundColor || styles.backgroundColor;
  return getComplianceFooterContrastColor(bg);
}

export interface ComplianceFooterRenderOptions {
  variant?: ComplianceFooterVariant | string | null;
  addressHtml: string;
  showAddress?: boolean;
  showBadge?: boolean;
  textColor: string;
  fontFamily: string;
}

function badgeLink(textColor: string, fontSize = '12px'): string {
  return `<a href="${LANDING_WEB_ORIGIN}" style="color: ${textColor}; text-decoration: none; font-weight: 500; font-size: ${fontSize};">Powered by Scubamail</a>`;
}

function unsubLink(textColor: string, fontSize = '12px', underline = true): string {
  const deco = underline ? 'underline' : 'none';
  return `<a href="${UNSUBSCRIBE_MERGE_TAG}" style="color: ${textColor}; text-decoration: ${deco}; font-size: ${fontSize};">Unsubscribe from these emails</a>`;
}

export function renderComplianceFooterHTML(options: ComplianceFooterRenderOptions): string {
  const variant = normalizeComplianceFooterVariant(options.variant);
  const showAddress = options.showAddress !== false;
  const showBadge = options.showBadge !== false;
  const { addressHtml, textColor, fontFamily } = options;

  const base = `font-family: ${fontFamily}; text-align: center; color: ${textColor};`;

  if (variant === 'divided') {
    const row = [unsubLink(textColor, '11px'), showBadge ? badgeLink(textColor, '10px') : '']
      .filter(Boolean)
      .join('<span style="margin: 0 10px; opacity: 0.35;">|</span>');
    return `<div style="${base} font-size: 12px; margin-top: 32px; padding-top: 16px;">
      ${showAddress ? `<p style="margin: 0 0 14px 0; line-height: 1.5;">${addressHtml}</p>` : ''}
      <p style="margin: 0; font-size: 11px;">${row}</p>
    </div>`;
  }

  return `<div style="${base} font-size: 12px; margin-top: 32px; padding-top: 16px;">
    ${showAddress ? `<p style="margin: 0 0 8px 0; line-height: 1.5;">${addressHtml}</p>` : ''}
    <p style="margin: 8px 0 16px 0;">${unsubLink(textColor)}</p>
    ${showBadge ? `<p style="margin: 16px 0 0 0;">${badgeLink(textColor)}</p>` : ''}
  </div>`;
}
